import { CONSTANTS, HELPERS } from '@/utils';
import { defaultSizes, generateSrcSet, ipxUrl } from '@/utils/ipix';
import { format, parseISO } from 'date-fns';
import { useEffect, useMemo } from 'react';

import ImgWithFallback from '@/components/atoms/ImgWithFallback';
import useCountdown from '@/hooks/useCountdown';
import { id } from 'date-fns/locale';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FaPlayCircle } from 'react-icons/fa'; 
import { Link } from 'react-router-dom';
import Button from '../Button/Button';
import ConditionalWrapper from '../ConditionalWrapper/ConditionalWrapper';
import ProgressBar from '../ProgressBar/ProgressBar';
import { useSelector } from 'react-redux';

const ProductCard = ({
  title,
  progress,
  image,
  bookingCode,
  earlyBirdDate,
  basePrice,
  unitPrice,
  price,
  discount,
  heldAt,
  earlyTicketPrice,
  instructor,
  type,
  heldAtFormatted,
  location,
  linkTo,
  onClick,
  onEnroll,
  enrolled,
  has_subscription,
  completed,
  className,
  showPrice = true,
  eventType,
  eventKind,
  eventPackage,
  sessionFormatted,
  searchQuery,
  isPurchased = false,
  thumbnailVariations,
}) => {
  const { t } = useTranslation();

  const setting = useSelector((state) => state.client.payload?.available_order);
  const showPriceSetting = showPrice && setting && setting?.includes('BUY_COURSE');

  const IS_EVENT = Boolean(type && type.id === CONSTANTS.PRODUCT_TYPE.EVENT.id);
  const IS_HAS_PROGRESS = progress !== undefined && !Number.isNaN(progress);

  const priceCalculations = useMemo(() => {
    if (!IS_EVENT) return { finalPrice: price, displayPrice: price };

    const ticketPrice = Number(price) || 0;
    const sellPriceValue = Number(basePrice) || 0;
    const discountValue = Number(discount) || 0;
    const earlyBirdPrice = Number(earlyTicketPrice) || 0;

    // Tambahkan pengecekan tanggal early bird
    const isEarlyBirdDateValid = (x) => {
      if (!x) return false;

      try {
        const validUntil = new Date(earlyBirdDate);
        const currentDate = new Date();
        return currentDate <= validUntil;
      } catch (error) {
        return false;
      }
    };

    const useEarlyBirdPrice =
      earlyBirdPrice > 0 && earlyBirdPrice < ticketPrice && isEarlyBirdDateValid(earlyBirdDate);

    let finalDisplayPrice;
    let computedDiscountPercentage = discountValue;

    if (useEarlyBirdPrice) {
      finalDisplayPrice = earlyBirdPrice;
      // Hitung discount early bird berdasarkan perbedaan harga
      computedDiscountPercentage = Math.round(((ticketPrice - earlyBirdPrice) / ticketPrice) * 100);
    } else if (sellPriceValue > 0) {
      finalDisplayPrice = sellPriceValue;
    } else {
      finalDisplayPrice = discountValue > 0 ? ticketPrice * (1 - discountValue / 100) : ticketPrice;
    }

    return {
      finalPrice: Math.round(finalDisplayPrice),
      displayPrice: Math.round(finalDisplayPrice),
      originalPrice: ticketPrice,
      isEarlyBird: useEarlyBirdPrice,
      earlyBirdPrice: Math.round(earlyBirdPrice),
      discountPercentage: computedDiscountPercentage,
    };
  }, [IS_EVENT, price, basePrice, discount, earlyTicketPrice, earlyBirdDate]);

  const formatDiscount = (discountValue) => {
    if (typeof discountValue === 'string' && discountValue.endsWith('.00')) {
      return Number.parseInt(discountValue, 10);
    }

    return discountValue;
  };

  const formatEventDate = () => {
    if (!sessionFormatted) return '';

    try {
      // Handle different input formats
      const parts = sessionFormatted.split(' - ');
      if (parts.length !== 2) return sessionFormatted;

      // Check if the second part has a date (contains a year)
      const hasFullEndDate = parts[1].includes('2025') || parts[1].includes('2024') || parts[1].includes('2023');

      // Parse start date
      const startParts = parts[0].split(', ');
      const startDateStr = startParts[0]; // "23 Apr 2025"
      const startDateParts = startDateStr.split(' '); // ["23", "Apr", "2025"]
      const startDay = startDateParts[0];
      const startMonth = startDateParts[1];
      const startYear = startDateParts[2];

      // Convert month name to month number
      let monthNumber;
      try {
        monthNumber = HELPERS.getMonthNumber(startMonth);
      } catch (e) {
        const monthMap = {
          jan: '01',
          feb: '02',
          mar: '03',
          apr: '04',
          mei: '05',
          jun: '06',
          jul: '07',
          agu: '08',
          ags: '08',
          sep: '09',
          okt: '10',
          nov: '11',
          des: '12',

          may: '05',
          aug: '08',
          oct: '10',
          dec: '12',
        };
        monthNumber = monthMap[startMonth] || startMonth;
      }

      const startDate = parseISO(`${startYear}-${monthNumber}-${startDay}`);

      // Format start date in Indonesian
      const dayName = format(startDate, 'EEEE', { locale: id }); // "Rabu"
      const dayNumber = format(startDate, 'd', { locale: id }); // "23"
      const monthShort = format(startDate, 'MMM', { locale: id }).substring(0, 3); // "Apr"
      // If it's just time in the second part, it's a same-day event
      if (!hasFullEndDate) {
        // Same day format: "Rabu, 23 Apr 2025"
        return `${dayName}, ${dayNumber} ${monthShort} ${startYear}`;
      }

      // For different days, continue with original logic
      const endParts = parts[1].split(', ');
      const endDateStr = endParts[0]; // "07 Mar 2025"
      const endDateParts = endDateStr.split(' '); // ["07", "Mar", "2025"]
      const endDay = endDateParts[0];
      const endMonth = endDateParts[1];
      const endYear = endDateParts[2];

      // Check if dates are the same
      const isSameDay = startDay === endDay && startMonth === endMonth && startYear === endYear;
      if (isSameDay) {
        return `${dayName}, ${dayNumber} ${monthShort} ${startYear}`;
      }

      // Different days logic
      let endMonthNumber;
      try {
        endMonthNumber = HELPERS.getMonthNumber(endMonth);
      } catch (e) {
        const monthMap = {
          Jan: '01',
          Feb: '02',
          Mar: '03',
          Apr: '04',
          May: '05',
          Jun: '06',
          Jul: '07',
          Aug: '08',
          Sep: '09',
          Oct: '10',
          Nov: '11',
          Dec: '12',
        };
        endMonthNumber = monthMap[endMonth] || endMonth;
      }

      const endDate = parseISO(`${endYear}-${endMonthNumber}-${endDay}`);
      const endDayName = format(endDate, 'EEEE', { locale: id });
      const endDayNumber = format(endDate, 'd', { locale: id });
      const endMonthShort = format(endDate, 'MMM', { locale: id }).substring(0, 3);

      return `${dayName}, ${dayNumber} ${monthShort} - ${endDayName}, ${endDayNumber} ${endMonthShort} ${endYear}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return sessionFormatted;
    }
  };

  const getEventTypeClasses = (typeValue) => {
    if (typeValue === 'online') return 'bg-[#FFF6ED] text-[#C4320A] w-[67px] h-[26px] py-1 px-3 rounded-full';
    if (typeValue === 'offline')
      return 'bg-[#EFF8FF] text-[#175CD3] font-medium text-sm leading-[18px] text-center';
    return 'bg-[#F4F3FF] text-[#5925DC] font-medium text-sm leading-[18px] text-center';
  };

  const getEventStatus = () => {
    if (!sessionFormatted && !heldAt) return { isComingSoon: false, isOngoing: false };

    try {
      let eventStartDateTime;
      let eventEndDateTime;

      // Parse dari sessionFormatted
      if (sessionFormatted) {
        // Format contoh: "09 Apr 2025, 02:00 - 23:00"
        const sessionParts = sessionFormatted.split(' - ');
        if (sessionParts.length < 1) return { isComingSoon: false, isOngoing: false };

        const datePart = sessionParts[0]; // "09 Apr 2025, 02:00"
        const [dateStr, timeStr] = datePart.split(', ');

        if (!dateStr || !timeStr) return { isComingSoon: false, isOngoing: false };

        // Parse komponen tanggal
        const dateComponents = dateStr.split(' '); // ["09", "Apr", "2025"]
        if (dateComponents.length !== 3) return { isComingSoon: false, isOngoing: false };

        const day = dateComponents[0];
        const month = dateComponents[1];
        const year = dateComponents[2];

        // Dapatkan nomor bulan
        let monthNumber;
        try {
          monthNumber = HELPERS.getMonthNumber(month);
        } catch (e) {
          const monthMap = {
            Jan: '01',
            Feb: '02',
            Mar: '03',
            Apr: '04',
            May: '05',
            Jun: '06',
            Jul: '07',
            Aug: '08',
            Sep: '09',
            Oct: '10',
            Nov: '11',
            Dec: '12',
            // Varian lowercase
            jan: '01',
            feb: '02',
            mar: '03',
            apr: '04',
            may: '05',
            jun: '06',
            jul: '07',
            aug: '08',
            sep: '09',
            oct: '10',
            nov: '11',
            dec: '12',
          };
          monthNumber = monthMap[month.toLowerCase()] || '01';
        }

        // Parse komponen waktu
        const [hours, minutes] = timeStr.split(':').map((num) => Number.parseInt(num, 10));

        // Buat objek datetime lengkap untuk waktu mulai
        eventStartDateTime = new Date(
          Number.parseInt(year, 10),
          Number.parseInt(monthNumber, 10) - 1, // Bulan di JS dimulai dari 0
          Number.parseInt(day, 10),
          hours,
          minutes
        );

        // Parse waktu akhir jika tersedia
        if (sessionParts.length > 1 && sessionParts[1].includes(':')) {
          const endTimeStr = sessionParts[1];
          const [endHours, endMinutes] = endTimeStr.split(':').map((num) => Number.parseInt(num, 10));

          // Buat datetime akhir (hari yang sama)
          eventEndDateTime = new Date(
            Number.parseInt(year, 10),
            Number.parseInt(monthNumber, 10) - 1,
            Number.parseInt(day, 10),
            endHours,
            endMinutes
          );
        } else {
          // Jika tidak ada waktu akhir, set waktu akhir ke waktu mulai + 2 jam
          eventEndDateTime = new Date(eventStartDateTime);
          eventEndDateTime.setHours(eventStartDateTime.getHours() + 2);
        }
      }
      // Atau gunakan heldAt jika tersedia
      else if (heldAt) {
        eventStartDateTime = new Date(heldAt);
        // Set default waktu akhir ke waktu mulai + 2 jam
        eventEndDateTime = new Date(eventStartDateTime);
        eventEndDateTime.setHours(eventStartDateTime.getHours() + 2);
      }

      if (!eventStartDateTime || Number.isNaN(eventStartDateTime.getTime())) {
        return { isComingSoon: false, isOngoing: false };
      }

      const currentDateTime = new Date();
      const diffStartMs = eventStartDateTime - currentDateTime;
      const diffStartHours = diffStartMs / (1000 * 60 * 60);

      // Cek apakah event sedang berlangsung
      const isOngoing = currentDateTime >= eventStartDateTime && currentDateTime <= eventEndDateTime;

      // Tentukan tipe countdown berdasarkan waktu yang tersisa
      // HANYA tampilkan countdown jika kurang dari 24 jam lagi
      let isComingSoon = false;
      if (diffStartHours > 0 && diffStartHours <= 24) {
        isComingSoon = true;
      }

      return { isComingSoon, isOngoing };
    } catch (error) {
      console.error('Error checking event date:', error);
      return { isComingSoon: false, isOngoing: false };
    }
  };

  const [timeLeft, setCountdownEndTime] = useCountdown();

  const eventImageSources = useMemo(() => {
    const baseSrc = thumbnailVariations?.jpg || image;
    if (!baseSrc) return { src: null, srcSetWebp: null, fallbackSrc: null };

    const widths = [240, 480];

    return {
      src: ipxUrl(baseSrc, { width: 480 }),
      srcSetWebp: thumbnailVariations?.webp
        ? generateSrcSet(thumbnailVariations.webp, widths, { format: 'webp', quality: 90 })
        : generateSrcSet(baseSrc, widths, { format: 'webp', quality: 90 }),
      fallbackSrc: baseSrc,
    };
  }, [thumbnailVariations, image]);

  const courseImageSources = useMemo(() => {
    if (!image) return { src: null, srcSetWebp: null, fallbackSrc: null };

    const widths = [240, 320, 480];

    return {
      src: ipxUrl(image, { width: widths[widths.length - 1] }),
      srcSetWebp: generateSrcSet(image, widths, { format: 'webp', quality: 90 }),
      fallbackSrc: image,
    };
  }, [image]);

  useEffect(() => {
    if (IS_EVENT) {
      try {
        const eventStatus = getEventStatus();
        const { isComingSoon, isOngoing } = eventStatus;

        if (isComingSoon) {
          // Extract event start time from sessionFormatted
          let eventStartTime;

          if (sessionFormatted) {
            const sessionParts = sessionFormatted.split(' - ');
            if (sessionParts.length > 0) {
              const datePart = sessionParts[0]; // "09 Apr 2025, 20:00"
              const [dateStr, timeStr] = datePart.split(', ');

              if (dateStr && timeStr) {
                // Parse date components
                const dateComponents = dateStr.split(' '); // ["09", "Apr", "2025"]
                if (dateComponents.length === 3) {
                  const day = dateComponents[0];
                  const month = dateComponents[1];
                  const year = dateComponents[2];

                  // Get month number
                  let monthNumber;
                  try {
                    monthNumber = HELPERS.getMonthNumber(month);
                  } catch (e) {
                    const monthMap = {
                      Jan: '01',
                      Feb: '02',
                      Mar: '03',
                      Apr: '04',
                      May: '05',
                      Jun: '06',
                      Jul: '07',
                      Aug: '08',
                      Sep: '09',
                      Oct: '10',
                      Nov: '11',
                      Dec: '12',
                      jan: '01',
                      feb: '02',
                      mar: '03',
                      apr: '04',
                      mei: '05',
                      jun: '06',
                      jul: '07',
                      agu: '08',
                      sep: '09',
                      okt: '10',
                      nov: '11',
                      des: '12',
                    };
                    monthNumber = monthMap[month.toLowerCase()] || '01';
                  }

                  // Parse time
                  const [hours, minutes] = timeStr.split(':').map((num) => Number.parseInt(num, 10));

                  // Create Date object for event time
                  eventStartTime = new Date(
                    Number.parseInt(year, 10),
                    Number.parseInt(monthNumber, 10) - 1,
                    Number.parseInt(day, 10),
                    hours,
                    minutes
                  );
                }
              }
            }
          } else if (heldAt) {
            // Use heldAt if sessionFormatted is not available
            eventStartTime = new Date(heldAt);
          }

          // Set the countdown end time if we successfully got an event time
          if (eventStartTime && !Number.isNaN(eventStartTime.getTime())) {
            setCountdownEndTime(eventStartTime);
          }
        }
      } catch (error) {
        console.error('Error setting up countdown:', error);
      }
    }

    // No need to return a cleanup function as useCountdown handles it
  }, [IS_EVENT, sessionFormatted, heldAt, setCountdownEndTime]);

  if (IS_EVENT) {
    return (
      <div className="overflow-hidden  w-full max-w-[435px] transition-all duration-200 bg-white shadow-sm hover:shadow-md rounded-3xl">
        <ConditionalWrapper
          condition={!!linkTo}
          wrapper={(childs) => (
            <Link to={linkTo} onClick={onClick} className="block">
              {childs}
            </Link>
          )}
        >
          {/* Image container dengan badge */}
          <div className="relative" style={{ height: 224 }}>
            {(() => {
              const eventStatus = getEventStatus();
              const { isComingSoon, isOngoing } = eventStatus;

              if (isComingSoon) {
                return (
                  <div className="absolute top-0 left-0 z-10 flex items-center gap-2 font-secondary bg-tertiary w-[165px] h-10 py-2 px-4 rounded-tl-3xl rounded-br-3xl text-white">
                    <span className="font-normal text-base">Segera</span>
                    <span className="font-bold text-base">
                      {timeLeft ? `${timeLeft.hours}:${timeLeft.minutes}:${timeLeft.seconds}` : '00:00:00'}
                    </span>
                  </div>
                );
              }
              if (isOngoing) {
                return (
                  <div className="absolute top-0 left-0 z-10 flex items-center gap-2 font-secondary bg-green-600 w-[220px] h-10 py-2 px-4 rounded-tl-3xl rounded-br-3xl text-white">
                    <span className="font-normal text-base">Berlangsung</span>
                  </div>
                );
              }
              return null;
            })()}

            {/* Use ImgWithFallback with ipx sources */}
            <ImgWithFallback
              className="object-cover object-center w-full h-full"
              src={eventImageSources.src}
              srcSetWebp={eventImageSources.srcSetWebp}
              fallbackSrc={eventImageSources.fallbackSrc}
              sizes={defaultSizes}
              alt={title}
              loading="lazy"
              style={{
                borderTopLeftRadius: '24px',
                borderTopRightRadius: '24px',
                height: '224px',
              }}
            />

            {/* Event kind badge (Bootcamp, Live Class) */}
            {eventKind && (
              <div className="absolute bottom-4 left-4 inline-block px-3 py-1 mb-2 text-sm font-medium rounded-md font-secondary text-[#C11574] bg-[#FDF2FA]">
                {eventKind}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex flex-col justify-between bg-white min-h-[242px] p-4 rounded-b-3xl">
            {/* Upper content */}
            <div className="space-y-2 flex-grow">
              {/* Title */}
              <h3 className="font-bold text-gray-800 line-clamp-2">
                {searchQuery && title ? (
                  <>
                    {title.split(new RegExp(`(${searchQuery})`, 'gi')).map((part, i) =>
                      part.toLowerCase() === searchQuery?.toLowerCase() ? (
                        <mark key={i} className="bg-yellow-100 px-0.5 rounded-sm">
                          {part}
                        </mark>
                      ) : (
                        part
                      )
                    )}
                  </>
                ) : (
                  title
                )}
              </h3>

              {/* Date */}
              {sessionFormatted && <p className="text-sm text-gray-600 font-secondary">{formatEventDate()}</p>}

              {/* Event type badge */}
              {eventType && (
                <div className="mb-2 flex flex-wrap gap-1">
                  <span
                    className={`inline-flex items-center justify-center font-secondary text-sm ${getEventTypeClasses(
                      eventType
                    )}`}
                    style={{ fontSize: '14px', padding: '4px 12px', borderRadius: '16px' }}
                  >
                    {eventType.charAt(0).toUpperCase() + eventType.slice(1)}
                  </span>
                  {eventPackage === 3 && (
                    <span
                      className="inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-full font-secondary text-[#027A48] bg-[#ECFDF3] w-[140px] h-[26px] text-center leading-[18px]"
                      style={{ fontSize: '14px', padding: '4px 12px', borderRadius: '16px' }}
                    >
                      Bonus kursus
                    </span>
                  )}
                  {eventPackage === 2 && (
                    <span
                      className="inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-full font-secondary text-[#B54708] bg-[#FFF4CC] w-[160px] h-[26px] text-center leading-[18px]"
                      style={{ fontSize: '14px', padding: '4px 12px', borderRadius: '16px' }}
                    >
                      Kursus bersyarat
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="pb-1 pt-4 flex flex-col justify-end">
              {/* Price section */}
              <div className="flex items-center justify-between">
                <div>
                  {/* Jika sudah dibeli, tampilkan "Sudah Dibeli" dengan style yang diminta */}
                  {isPurchased ? (
                    <div className="text-primary-700 font-secondary text-lg font-bold leading-7 align-middle">
                      Sudah Dibeli
                    </div>
                  ) : (
                    /* Jika belum dibeli, tampilkan informasi harga seperti biasa */
                    <>
                      {showPriceSetting && (
                        <>
                          {/* Tampilkan badge Early Bird jika aktif */}
                          {priceCalculations.isEarlyBird && (
                            <div className="px-2 py-1 text-xs text-white rounded-full bg-blue-500 font-secondary inline-block mb-1 mr-2">
                              Early Bird
                            </div>
                          )}
                          {(priceCalculations.isEarlyBird || Number(discount) > 0) && (
                            <div className="px-2 py-1 text-xs font-bold text-white rounded-full bg-tertiary font-secondary inline-block mb-1">
                              Disc{' '}
                              {priceCalculations.isEarlyBird
                                ? priceCalculations.discountPercentage
                                : formatDiscount(discount)}
                              %
                            </div>
                          )}

                          {/* Tampilkan harga asli sebagai strike-through */}
                          {(Number(discount) > 0 || priceCalculations.isEarlyBird) && (
                            <div className="font-bold text-gray-400 line-through font-secondary ml-2 inline-block">
                              {`Rp ${HELPERS.numberFormat(priceCalculations.originalPrice)}`}
                            </div>
                          )}
                        </>
                      )}

                      {/* Current price display */}
                      <div className="text-lg font-bold text-primary-700 font-secondary">
                        {priceCalculations.displayPrice === 0
                          ? t('free')
                          : `Rp${HELPERS.numberFormat(priceCalculations.displayPrice)}`}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Organizer - mt-auto should keep this at the bottom of this section */}
              {instructor && (
                <div className="mt-auto pt-2 flex items-center">
                  <span className="font-medium text-gray-600 mr-1">Acara oleh</span>{' '}
                  <span className="text-gray-app-700 font-secondary font-bold text-base leading-6">
                    {instructor}
                  </span>
                </div>
              )}
            </div>
          </div>
        </ConditionalWrapper>
      </div>
    );
  }

  // ORIGINAL CODE FOR COURSE - UPDATED with ImgWithFallback
  return (
    <div className={`${className}`} title={title}>
      <ConditionalWrapper
        condition={!!linkTo}
        wrapper={(childs) => (
          <Link to={linkTo} onClick={onClick}>
            {childs}
          </Link>
        )}
      >
        <div className="h-full border rounded-2xl transition-all shadow-sm flex flex-col overflow-hidden bg-white">
          <div className="h-48">
            <ImgWithFallback
              className="object-cover object-center w-full h-full"
              src={courseImageSources.src}
              srcSetWebp={courseImageSources.srcSetWebp}
              fallbackSrc={courseImageSources.fallbackSrc}
              sizes="(min-width: 1024px) 480px, (min-width: 768px) 320px, 240px"
              alt={title}
              loading="lazy"
            />
          </div>
          <div className="flex-grow p-4 min-h-[65px]">
            <h3 className="font-bold line-clamp-2">{title}</h3>
            <div>
              {(heldAt || heldAtFormatted) && (
                <p className="text-xs font-secondary">
                  {heldAtFormatted || format(new Date(heldAt), 'dd MMMM yyyy')}
                </p>
              )}
              {location && <p className="text-xs font-secondary">@ {location}</p>}
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                {bookingCode && <p className="text-xs text-gray-500">{bookingCode}</p>}
                {!has_subscription && !enrolled && discount > 0 && (showPriceSetting || !has_subscription) ? (
                  <div className="flex items-center justify-start gap-2 mb-1 text-xs">
                    <div className="px-2 py-1 text-white rounded-full bg-tertiary font-secondary">
                      Disc {formatDiscount(discount)}%
                    </div>
                    {unitPrice > 0 && (
                      <div className="font-bold text-gray-400 line-through font-secondary">{`Rp ${HELPERS.numberFormat(
                        unitPrice
                      )}`}</div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                {!has_subscription && !enrolled && price && (showPriceSetting || !has_subscription) ? (
                  <div className="text-lg font-bold font-secondary text-primary-700">
                    {price === 0 && t('free')} {price > 0 && `Rp ${HELPERS.numberFormat(price)}`}
                  </div>
                ) : null}
              </div>
            </div>

            {enrolled && !IS_HAS_PROGRESS && (
              <div className="flex items-center gap-1 text-xs text-gray-400 font-secondary">
                <FaPlayCircle />
                <span>Enrolled</span>
              </div>
            )}

            <div>
              {IS_HAS_PROGRESS && enrolled && <ProgressBar progress={progress} size={ProgressBar.SIZE.SMALL} />}
              {IS_EVENT && instructor && <div className="font-semibold">Organizer: {instructor}</div>}
            </div>

            {!enrolled && has_subscription && (
              <Button variant={Button.VARIANTS.SECONDARY} className="w-full" onClick={onEnroll}>
                Mulai Belajar
              </Button>
            )}
          </div>
        </div>
      </ConditionalWrapper>
    </div>
  );
};

ProductCard.propTypes = {
  title: PropTypes.string,
  image: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  basePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unitPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  earlyTicketPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  linkTo: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  // Props khusus event - opsional
  eventType: PropTypes.string,
  eventKind: PropTypes.string,
  earlyBirdDate: PropTypes.string,
  eventPackage: PropTypes.number,
  sessionFormatted: PropTypes.string,
  searchQuery: PropTypes.string,
  isPurchased: PropTypes.bool,
  has_subscription: PropTypes.bool,
  thumbnailVariations: PropTypes.shape({
    webp: PropTypes.string,
    jpg: PropTypes.string,
  }),
};

ProductCard.defaultProps = {
  title: '',
  image: null,
  price: 0,
  basePrice: 0,
  unitPrice: 0,
  discount: 0,
  earlyTicketPrice: 0,
  linkTo: null,
  onClick: () => {},
  onEnroll: () => {},
  className: '',
  // Default untuk props event
  earlyBirdDate: '',
  eventType: '',
  eventKind: '',
  eventPackage: 0,
  sessionFormatted: '',
  searchQuery: '',
  isPurchased: false,
  thumbnailVariations: null,
};

export default ProductCard;
